/**
 * Note(Erfan):
 * Previously, we used a regular text field along with the "react-mentions" library to store mentions in a
 * standard format, using "@(name)[ID]"" syntax. However, after transitioning to "react-quill" and "quill-mentions",
 * the storage format for mentions has changed to some HTML values. Unfortunately, other parts of our application
 * still rely on the "@(name)[ID]"" format. To address this, we needed to develop converters that enable the conversion
 * between the storage format used by quill-mentions and our standard format. These converters allow us to maintain
 * mentions in the familiar format, while internally, within the Editor, they are manipulated using the HTML
 * format employed by quill-mentions.
 *
 * P.S: Ideally we shouldn't care about the format we're saving mentions, there should be no need to extract mentions
 * out of the text. We need to stores mentions separately in the comment row. However, it's not like that at the moment.
 */
import { find } from 'linkifyjs'
import { convertHtmlWithMentionsToStandardFormat, quillVariableObjectRegex } from '@omnea-app/requests/requestsFrontendClient'

export const formatCommentHtmlTextToStandardFormat = (html: string) =>
  removeNewLinesFromEnd(convertHtmlWithMentionsToStandardFormat(html))

// @(name)[any text]
const quillVariableUserIdRegex = /@\[([^\[]+)(?:\s\[([^\]]+)\])?\]\(([^)]+)\)/g

export function convertStandardFormatToHtmlWithMentions(inputString: string) {
  if (inputString === '') {
    return inputString
  }

  return inputString
    .replace(quillVariableObjectRegex, (_, value, ID) => {
      const hasError = ID.includes('isValid: false')

      if (hasError) {
        return `<span class="mention" data-index="0" data-denotation-char="@" data-error="${hasError ? 'true' : 'false'}" data-id="${ID}" data-value="${value}">﻿<span contenteditable="false">${value}</span>﻿</span>`
      }

      return `<span class="mention" data-index="0" data-denotation-char="@" data-id="${ID}" data-value="${value}">﻿<span contenteditable="false">${value}</span>﻿</span>`
    })
    .replace(quillVariableUserIdRegex, (_, fullName, email, userId) => {
      const userName = email ? `${fullName} [${email}]` : fullName
      return `<span class="mention" data-index="0" data-denotation-char="@" data-id="${userId}" data-value="${userName}">﻿<span contenteditable="false">${userName}</span>﻿</span>`
    })
}

export function removeNewLinesFromEnd(htmlString: string) {
  const regex = /<p><br><\/p>\s*$/
  while (regex.test(htmlString)) {
    htmlString = htmlString.replace(regex, '')
  }
  return htmlString
}

export function isHtmlTextEmpty(htmlString: string) {
  return removeNewLinesFromEnd(htmlString).trim().length === 0
}

export const isPastedUrl = (text: string) => {
  const trimmed = text.trim()
  const matches = find(trimmed)
  return matches.length === 1 && matches[0]?.value === trimmed && matches[0]?.type === 'url'
}

// Note: prepend 'http' to link in case user provided link doesn't have it (https://github.com/zenoamaro/react-quill/issues/529)
export const sanitizeUrl = (url: string) => {
  const VALID_URL_PREFIXES = ['http://', 'https://', 'mailto:', 'ftp://', 'tel:']
  const lower = url.toLowerCase()
  if (VALID_URL_PREFIXES.some((prefix) => lower.startsWith(prefix))) {
    return url
  }
  return `http://${url}`
}
